import {
  Building2,
  CalendarX,
  FileUp,
  Gift,
  Landmark,
  Languages,
  Percent,
  ShieldCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { COMMERCIAL_PRICING } from '@connexio/shared/constants';
import { Section } from './Section';
import styles from './TrustStrip.module.css';

/**
 * Capability strip in the slot where a SaaS page would put a customer logo wall.
 * Only facts that are true in the product today: no testimonials, logos, user
 * counts, ratings or awards. Add real testimonials here once customers have given
 * them (with permission); never invent them.
 */
const ITEMS: { key: string; icon: LucideIcon }[] = [
  { key: 'fortnox', icon: Landmark },
  { key: 'orgnr', icon: Building2 },
  { key: 'import', icon: FileUp },
  { key: 'vat', icon: Percent },
  { key: 'language', icon: Languages },
  { key: 'trial', icon: Gift },
  { key: 'cancel', icon: CalendarX },
  { key: 'eu', icon: ShieldCheck },
];

export function TrustStrip({ signupEnabled }: { signupEnabled: boolean }) {
  const { t } = useTranslation('marketing');
  // No trial promise while self-service sign-up is closed.
  const items = ITEMS.filter((item) => signupEnabled || item.key !== 'trial');

  return (
    // Extra top padding clears the hero visual, which bleeds down into this band.
    <Section tone="tint" className={styles.section}>
      <p className={styles.label}>
        {t('trust.label')}
      </p>
      {/* Two per row on a phone, four from md. Flex-wrap with fixed bases rather than a
          grid, so the seven-item variant (sign-up closed) centres its last row instead
          of leaving an empty cell. */}
      <ul className={styles.list}>
        {items.map(({ key, icon: Icon }) => (
          <li
            key={key}
            className={styles.item}
          >
            <Icon className={styles.icon} aria-hidden="true" />
            <span className={styles.text}>{t(`trust.items.${key}`, { days: COMMERCIAL_PRICING.trialDays })}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}
